import {
  GATEWAY_ERROR_KINDS,
  type GatewayErrorCodeTable,
} from '@/shared-responder-v2';

/**
 * Every unified-flow error code the survey schema can raise, mapped onto the
 * outcome the responder renders. Codes absent from this table are rethrown
 * unchanged, so an unrecognised backend failure surfaces as an error rather
 * than as a wrong-but-plausible screen.
 *
 * `SURVEY_MESSAGES_V2_MISSING_REQUIRED_QUESTIONS` is deliberately absent: the
 * backend removed it along with `submitSurvey`, so the required-questions block
 * is now driven by querying `surveyCompletionStatus` before the final continue,
 * never by reacting to a rejection.
 *
 * Every screener code collapses onto `ScreenerRequired` here: whichever one
 * rejected the key, the only recovery at session-start time is redoing the
 * screener. The screener's own mutations keep the fine-grained outcomes — see
 * `SCREENER_FLOW_GATEWAY_ERROR_CODES` below.
 */
export const SURVEY_GATEWAY_ERROR_CODES: GatewayErrorCodeTable = {
  SURVEY_MESSAGES_V2_PROJECT_NOT_UNIFIED: GATEWAY_ERROR_KINDS.ProjectNotUnified,
  SURVEY_INSIGHTS_PROJECT_NOT_UNIFIED: GATEWAY_ERROR_KINDS.ProjectNotUnified,

  SURVEY_MESSAGES_V2_PROJECT_NOT_PUBLISHED:
    GATEWAY_ERROR_KINDS.ProjectNotPublished,
  SURVEY_INSIGHTS_PROJECT_NOT_PUBLISHED:
    GATEWAY_ERROR_KINDS.ProjectNotPublished,

  LLM_GATEWAY_QUESTION_SESSION_ALREADY_COMPLETED:
    GATEWAY_ERROR_KINDS.QuestionAlreadyCompleted,

  SURVEY_MESSAGES_V2_JUMP_NOT_ALLOWED: GATEWAY_ERROR_KINDS.JumpNotAllowed,
  SURVEY_MESSAGES_V2_SELECTION_BOUNDS_VIOLATED:
    GATEWAY_ERROR_KINDS.SelectionBoundsViolated,

  SURVEY_MESSAGES_V2_INSIGHT_NOT_FOUND: GATEWAY_ERROR_KINDS.InsightNotFound,
  SURVEY_INSIGHTS_NOT_FOUND: GATEWAY_ERROR_KINDS.InsightNotFound,

  SURVEY_MESSAGES_V2_SESSION_EXPIRED: GATEWAY_ERROR_KINDS.SessionExpired,
  SURVEY_INSIGHTS_SESSION_EXPIRED: GATEWAY_ERROR_KINDS.SessionExpired,

  SURVEY_MESSAGES_V2_RESPONSE_LIMIT_REACHED:
    GATEWAY_ERROR_KINDS.ResponseLimitReached,
  SURVEY_INSIGHTS_RESPONSE_LIMIT_REACHED:
    GATEWAY_ERROR_KINDS.ResponseLimitReached,

  SURVEY_MESSAGES_V2_QUESTION_NOT_FOUND:
    GATEWAY_ERROR_KINDS.QuestionUnavailable,
  SURVEY_MESSAGES_V2_QUESTION_NOT_PRIMARY:
    GATEWAY_ERROR_KINDS.QuestionUnavailable,
  SURVEY_MESSAGES_V2_QUESTION_NOT_SKIPPABLE:
    GATEWAY_ERROR_KINDS.QuestionRequired,
  // A refused edit: the session restores the answer the backend still holds.
  SURVEY_MESSAGES_V2_EDIT_NOT_ALLOWED: GATEWAY_ERROR_KINDS.QuestionUnavailable,
  SURVEY_MESSAGES_V2_MESSAGE_NOT_FOUND: GATEWAY_ERROR_KINDS.QuestionUnavailable,
  SURVEY_INSIGHTS_QUESTION_NOT_FOUND: GATEWAY_ERROR_KINDS.QuestionUnavailable,

  LLM_GATEWAY_QUESTION_SESSION_INSPECT_UNAVAILABLE:
    GATEWAY_ERROR_KINDS.AnswerNotProcessed,

  SURVEY_MESSAGES_V2_MATERIALIZATION_FAILED:
    GATEWAY_ERROR_KINDS.SessionUnrecoverable,
  SURVEY_MESSAGES_V2_SESSION_RECOVERY_STATE_MISSING:
    GATEWAY_ERROR_KINDS.SessionUnrecoverable,

  SURVEY_INSIGHTS_SCREENER_SESSION_KEY_REQUIRED:
    GATEWAY_ERROR_KINDS.ScreenerRequired,
  SURVEY_SCREENERS_PROJECT_SESSION_KEY_INVALID:
    GATEWAY_ERROR_KINDS.ScreenerRequired,
  SURVEY_SCREENERS_SESSION_NOT_FOUND: GATEWAY_ERROR_KINDS.ScreenerRequired,
  SURVEY_SCREENERS_SESSION_DISQUALIFIED: GATEWAY_ERROR_KINDS.ScreenerRequired,
  SURVEY_SCREENERS_SESSION_COMPLETED: GATEWAY_ERROR_KINDS.ScreenerRequired,
};

/**
 * The screener's own mutations need the fine-grained outcomes the unified
 * table deliberately collapses: mid-screener, "session not found" restarts,
 * "answer invalid" keeps the respondent on the question, "disqualified"
 * screens them out, and a screener that cannot be taken at all bypasses.
 *
 * `SCREENER_DISABLED` is that last case as a race: `hasScreener` already
 * excludes a disabled screener, so the flow only reaches `addScreenerSession`
 * for one that was turned off after the page loaded. Bypassing hands the
 * respondent the project directly, which is what the author asked for.
 *
 * `SCREENER_NOT_FOR_CHANNEL` is the same race for the screener's audience: the
 * channel was dropped from it after the page loaded. `hasScreener` is asked
 * per channel, so this is a backstop, not the normal path.
 */
export const SCREENER_FLOW_GATEWAY_ERROR_CODES: GatewayErrorCodeTable = {
  SURVEY_SCREENERS_NO_SCREENER: GATEWAY_ERROR_KINDS.ScreenerNotAvailable,
  SURVEY_SCREENERS_SCREENER_DISABLED: GATEWAY_ERROR_KINDS.ScreenerNotAvailable,
  SURVEY_SCREENERS_SCREENER_NOT_FOR_CHANNEL:
    GATEWAY_ERROR_KINDS.ScreenerNotAvailable,
  SURVEY_SCREENERS_NO_QUESTIONS: GATEWAY_ERROR_KINDS.ScreenerNotAvailable,
  SURVEY_SCREENERS_ANSWER_INVALID: GATEWAY_ERROR_KINDS.ScreenerAnswerInvalid,
  SURVEY_SCREENERS_SESSION_NOT_FOUND:
    GATEWAY_ERROR_KINDS.ScreenerSessionNotFound,
  SURVEY_SCREENERS_SESSION_DISQUALIFIED:
    GATEWAY_ERROR_KINDS.ScreenerSessionDisqualified,
  SURVEY_SCREENERS_SESSION_COMPLETED:
    GATEWAY_ERROR_KINDS.ScreenerSessionCompleted,
};
